/**
 * Prototype scaffolding, not product.
 *
 * Several fixtures exist only to demonstrate a state a reviewer would
 * otherwise never stumble on — a mutual wave, a wave that hasn't been
 * returned, a memorial, an item still in moderation. This registry is the
 * single place those are described, so the in-context chips and the states
 * index can never drift apart.
 */

export interface PrototypeTarget {
  view: 'join' | 'invite' | 'expired' | 'dashboard'
  section?: 'home' | 'people' | 'archive' | 'voices' | 'profile'
  profileName?: string
  sparse?: boolean
}

export interface PrototypeState {
  id: string
  /** Short label for the chip. */
  label: string
  /** One line explaining what the reviewer is looking at. */
  note: string
  group: 'Profiles' | 'Contributions' | 'Community' | 'Entry points'
  /** Set when the state is demonstrated by one member's fixture. */
  memberName?: string
  target: PrototypeTarget
}

export const PROTOTYPE_STATES: PrototypeState[] = [
  {
    id: 'mutual-wave',
    label: 'Mutual wave',
    note: 'Both of you have waved, so the social links on this profile are unlocked.',
    group: 'Profiles',
    memberName: 'Theresa Molina',
    target: { view: 'dashboard', section: 'profile', profileName: 'Theresa Molina' },
  },
  {
    id: 'wave-pending',
    label: 'Wave not returned',
    note: 'They have added links, but the handshake is incomplete. This is the locked state.',
    group: 'Profiles',
    memberName: 'Anita Cruz',
    target: { view: 'dashboard', section: 'profile', profileName: 'Anita Cruz' },
  },
  {
    id: 'memorial',
    label: 'Memorial profile',
    note: 'A member who has died. No wave, tributes instead of contact, and the family’s giving request.',
    group: 'Profiles',
    memberName: 'Sean Duffy',
    target: { view: 'dashboard', section: 'profile', profileName: 'Sean Duffy' },
  },
  {
    id: 'own-profile',
    label: 'Your own profile',
    note: 'The visibility control and the badges you have earned, in place of the wave.',
    group: 'Profiles',
    memberName: 'Ray Mendel',
    target: { view: 'dashboard', section: 'profile', profileName: 'Ray Mendel' },
  },
  {
    id: 'two-stretches',
    label: 'Involved twice, decades apart',
    note: 'A program in the 1970s, back as a volunteer from 2009: two separate segments on the chart.',
    group: 'Profiles',
    memberName: 'Marion Alvarez',
    target: { view: 'dashboard', section: 'people' },
  },
  {
    id: 'still-involved',
    label: 'Still involved today',
    note: 'A supporter with an open-ended span, which reads as “–now” rather than ending this year.',
    group: 'Profiles',
    memberName: 'Howard Pike',
    target: { view: 'dashboard', section: 'people' },
  },
  {
    id: 'in-review',
    label: 'Awaiting staff review',
    note: 'A memory you submitted, shown as “In review” until a staff member approves it.',
    group: 'Contributions',
    target: { view: 'dashboard', section: 'archive' },
  },
  {
    id: 'unnamed',
    label: 'Unidentified people',
    note: 'Photographs and a recording with people nobody has named yet. This is the contribution ask.',
    group: 'Contributions',
    target: { view: 'dashboard', section: 'archive' },
  },
  {
    id: 'sparse',
    label: 'Twelve-member community',
    note: 'Every screen at launch scale, with honest sparse copy instead of empty modules.',
    group: 'Community',
    target: { view: 'dashboard', section: 'home', sparse: true },
  },
  {
    id: 'invite-landing',
    label: 'Invite landing',
    note: 'What someone sees when a member invites them by name. Normally reached by email.',
    group: 'Entry points',
    target: { view: 'invite' },
  },
  {
    id: 'expired-link',
    label: 'Expired link',
    note: 'A sign-in link older than seven days. Deliberately unalarmed.',
    group: 'Entry points',
    target: { view: 'expired' },
  },
]

export const PROTOTYPE_GROUPS: PrototypeState['group'][] = [
  'Profiles',
  'Contributions',
  'Community',
  'Entry points',
]

/** The state a given member's fixture demonstrates, if any. */
export function prototypeStateFor(memberName: string): PrototypeState | undefined {
  return PROTOTYPE_STATES.find((state) => state.memberName === memberName)
}
